import {formatIdOrCompanyNumber} from '@sollapay/domain';
import {Avatar, Badge, Table, Typography, nodeColumn} from '@sollapay/ui/components';
import {formatDateLocalized} from '@sollapay/utils';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {getPartyAvatarShape} from '@/utils';
import {getInitials} from '@/utils/get-initials.utils';

import type {FatcaCrsBeneficiarySnapshotDTO} from '@sollapay/types';
import type {FC} from 'react';

export interface FatcaCrsBeneficiaryTableProps {
  rows: FatcaCrsBeneficiarySnapshotDTO[];
}

export const FatcaCrsBeneficiaryTable: FC<FatcaCrsBeneficiaryTableProps> = ({rows}) => {
  const {t, i18n} = useTranslation('trustAccounts');

  const columns = useMemo(
    () => [
      nodeColumn<FatcaCrsBeneficiarySnapshotDTO>({
        id: 'name',
        header: t('activation.packages.compliance.supplementary.table.name'),
        accessorFn: row => row.name,
        cell: row => (
          <div className="flex items-center gap-2">
            <Avatar
              initials={getInitials(row.name)}
              seed={row.idOrCompanyNumber}
              size="sm"
              shape={getPartyAvatarShape(row.partyType)}
            />
            <Typography size="sm">{row.name}</Typography>
          </div>
        )
      }),
      nodeColumn<FatcaCrsBeneficiarySnapshotDTO>({
        id: 'role',
        header: t('activation.packages.compliance.supplementary.table.role'),
        accessorFn: row => row.role,
        cell: row => (
          <Badge variant="neutral" size="xs">
            {t(`partyRole.${row.role}` as Parameters<typeof t>[0])}
          </Badge>
        )
      }),
      nodeColumn<FatcaCrsBeneficiarySnapshotDTO>({
        id: 'idNumber',
        header: t('activation.packages.compliance.supplementary.table.idNumber'),
        enableSorting: false,
        cell: row => (
          <Typography size="sm" dir="ltr">
            {formatIdOrCompanyNumber(row, i18n.language)}
          </Typography>
        )
      }),
      nodeColumn<FatcaCrsBeneficiarySnapshotDTO>({
        id: 'birthDate',
        header: t('activation.packages.compliance.supplementary.table.birthDate'),
        accessorFn: row => row.birthOrIncorporationDate ?? '',
        cell: row =>
          row.birthOrIncorporationDate ? (
            <Typography size="sm">
              {formatDateLocalized(row.birthOrIncorporationDate, i18n.language)}
            </Typography>
          ) : (
            <Typography size="sm" color="tertiary">
              —
            </Typography>
          )
      }),
      nodeColumn<FatcaCrsBeneficiarySnapshotDTO>({
        id: 'address',
        header: t('activation.packages.compliance.supplementary.table.address'),
        enableSorting: false,
        cell: row =>
          row.address ? (
            <Typography size="sm">{row.address}</Typography>
          ) : (
            <Typography size="sm" color="tertiary">
              —
            </Typography>
          )
      })
    ],
    [t, i18n.language]
  );

  return <Table columns={columns} data={rows} />;
};
